/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import { Button, Tooltip, notification } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useDuplicateWorkerMutation, Worker } from '@/generated/graphql';
import { trackEvent } from '@/tracker';
import { AnalyticsEvents } from '@/analytics/events';
import { useAppSelector } from '../../stores/hooks';
import { departmentQuerySelector } from '../../stores/slices/department';
import routes from '../../utils/routes';
import PlaygroundReloadIcon from '../icons/PlaygroundReloadIcon';

interface IRetryRunProps {
  workerId: string;
  isPlayground: boolean;
}

function RetryRun({ workerId, isPlayground }: IRetryRunProps) {
  const [state, setState] = React.useState(false);
  const { department } = useAppSelector(departmentQuerySelector);
  const navigate = useNavigate();

  const onMutationError = (error: ApolloError) => {
    setState(false);
    notification.error({
      message: 'Error',
      description: error.message
    });
  };

  const [rerun, rerunResults] = useDuplicateWorkerMutation({
    onError: onMutationError
  });

  const duplicateWorker = rerunResults.data?.duplicateWorker;

  const getProcedureRunUrl = (worker: Worker) =>
    routes.procedure.getProcedureRunPageUrl(
      worker.departmentId!,
      worker.procedureId!,
      worker.id!
    );

  const initiateRun = () => {
    trackEvent(AnalyticsEvents.RETRY.ClickRunAgainButton);
    setState(true);
    rerun({
      variables: {
        workerId,
        departmentId: department.id,
        runWorker: !isPlayground
      }
    }).then(() => {
      setState(false);
    });
  };

  useEffect(() => {
    if (duplicateWorker) {
      if (isPlayground) {
        navigate(
          routes.playground.getPlaygroundPageUrl(
            department.id,
            duplicateWorker.id
          ),
          {
            replace: true
          }
        );
      } else {
        navigate(getProcedureRunUrl(duplicateWorker as Worker), {
          replace: true
        });
      }
    }
  }, [duplicateWorker]);

  const tooltipText = isPlayground
    ? 'Duplicate Playground'
    : 'Run Again (with same inputs)';

  return (
    <Tooltip title={tooltipText} placement="topRight">
      <Button
        type="text"
        data-cy="retry-run"
        shape="default"
        className="display-flex align-center justify-center"
        loading={rerunResults.loading}
        icon={
          isPlayground ? (
            <CopyOutlined
              style={{ fontSize: '20px', color: '#7A8E9D' }}
              spin={state}
              className="reload"
            />
          ) : (
            <PlaygroundReloadIcon spin={state} className="reload" />
          )
        }
        onClick={() => initiateRun()}
      />
    </Tooltip>
  );
}

export default RetryRun;
